import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismTable } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import GeneralTablesCellsTdCheckmark from 'lumen/components/general/tables/cells/td-checkmark';
import GeneralTablesCellsTdLink from 'lumen/components/general/tables/cells/td-link';
import { BLANK } from 'lumen/components/general/tables/cells/td-link-base';
import GeneralTablesCellsTdWithActions from 'lumen/components/general/tables/cells/td-with-actions';
import GeneralTablesCellsTdWithCustomClass from 'lumen/components/general/tables/cells/td-with-custom-class';
import ParityTablesCellsTdOta from 'lumen/components/parity/tables/cells/td-ota';
import type { ParityManagerIssue } from 'lumen/models';
import { ISSUE_TYPES, type IssueType } from 'lumen/models/parity-manager/issue';

const COLUMNS = {
  CHANNEL: 'channel',
  HOTEL: 'hotel',
  META: 'meta',
  SHOPPED_DATE: 'shopped_date',
  ARRIVAL_DATE: 'arrival_date',
  BRAND_RATE: 'brand_rate',
  META_RATE: 'meta_rate',
  CHANNEL_RATE: 'channel_rate',
  DIFFERENCE: 'difference',
  ROOT_CAUSE: 'root_cause',
  STATUS: 'status',
  ASSIGNED: 'assigned',
  ACTIONS: 'actions',
} as const;

type Column = (typeof COLUMNS)[keyof typeof COLUMNS];

interface ParityParityManagerTableIssuesSignature {
  Args: {
    issues: ParityManagerIssue[];
    onRowClick: (row: { event: Event; rowValue: CustomEmberTableRow }) => void;
    rootCauses: Option[];
    selectedMetric: IssueType;
    statuses: Option[];
  };
}

export default class ParityParityManagerTableIssues extends Component<ParityParityManagerTableIssuesSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked possibleColumns: Record<string, CustomEmberTableColumn>;

  get columns(): CustomEmberTableColumn[] {
    if (this.isMajorOtaView) {
      return [
        this.possibleColumns['CHANNEL'],
        this.possibleColumns['SHOPPED_DATE'],
        this.possibleColumns['ARRIVAL_DATE'],
        this.possibleColumns['BRAND_RATE'],
        this.possibleColumns['CHANNEL_RATE'],
        this.possibleColumns['DIFFERENCE'],
        this.possibleColumns['ROOT_CAUSE'],
        this.possibleColumns['STATUS'],
        this.possibleColumns['ASSIGNED'],
        this.possibleColumns['ACTIONS'],
      ] as CustomEmberTableColumn[];
    }

    if (this.isTestBookingView) {
      return [
        this.possibleColumns['HOTEL'],
        this.possibleColumns['SHOPPED_DATE'],
        this.possibleColumns['ARRIVAL_DATE'],
        this.possibleColumns['BRAND_RATE'],
        this.possibleColumns['CHANNEL_RATE'],
        this.possibleColumns['DIFFERENCE'],
        this.possibleColumns['WHOLESALER'],
        this.possibleColumns['STATUS'],
        this.possibleColumns['ASSIGNED'],
        this.possibleColumns['ACTIONS'],
      ] as CustomEmberTableColumn[];
    }

    if (this.isBaitAndSwitchView) {
      return [
        this.possibleColumns['HOTEL'],
        this.possibleColumns['META'],
        this.possibleColumns['SHOPPED_DATE'],
        this.possibleColumns['ARRIVAL_DATE'],
        this.possibleColumns['BRAND_RATE'],
        this.possibleColumns['META_RATE'],
        this.possibleColumns['CHANNEL_RATE'],
        this.possibleColumns['ROOT_CAUSE'],
        this.possibleColumns['STATUS'],
        this.possibleColumns['ASSIGNED'],
        this.possibleColumns['ACTIONS'],
      ] as CustomEmberTableColumn[];
    }

    return [] as CustomEmberTableColumn[];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBaitAndSwitchView() {
    return this.args.selectedMetric === ISSUE_TYPES.BAIT_AND_SWITCH;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMajorOtaView() {
    return this.args.selectedMetric === ISSUE_TYPES.MAJOR_OTA;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTestBookingView() {
    return this.args.selectedMetric === ISSUE_TYPES.TEST_BOOKING;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    if (!this.args.issues) {
      return [];
    }

    return this.args.issues?.map((issue) => ({
      id: issue.id,
      [COLUMNS.CHANNEL]: issue.channelId,
      [COLUMNS.HOTEL]: issue.hotelName,
      [COLUMNS.META]: issue.metaId,
      [COLUMNS.SHOPPED_DATE]: issue.formattedExtractDate,
      [COLUMNS.ARRIVAL_DATE]: issue.formattedArrivalDate,
      [COLUMNS.BRAND_RATE]: {
        redirectUrl: issue.baseRedirect,
        formattedValue: issue.formattedBaseRate,
        iconAfter: issue.baseCancellation ? 'rates-best-flex' : BLANK,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClickHandler: () => this.trackLinkExplored(COLUMNS.BRAND_RATE),
      },
      [COLUMNS.META_RATE]: {
        redirectUrl: issue.metasearchRedirect,
        formattedValue: issue.formattedMetaRate,
        iconAfter: issue.metaCancellation ? 'rates-best-flex' : BLANK,
      },
      [COLUMNS.CHANNEL_RATE]: {
        redirectUrl: issue.channelRedirect,
        formattedValue: issue.formattedChannelRate,
        iconAfter: issue.channelCancellation ? 'rates-best-flex' : BLANK,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClickHandler: () => this.trackLinkExplored(COLUMNS.CHANNEL_RATE),
      },
      [COLUMNS.DIFFERENCE]: {
        formattedValue: issue.formattedAbsoluteDifferenceWithBaserate,
        customClass: issue.differenceWithBaserateClass,
      },
      [COLUMNS.ROOT_CAUSE]: issue.formattedRootCause,
      [COLUMNS.STATUS]: issue.formattedStatus,
      [COLUMNS.ASSIGNED]: issue.hasAssignee,
      [COLUMNS.ACTIONS]: issue,
    }));
  }

  constructor(
    owner: Owner,
    args: ParityParityManagerTableIssuesSignature['Args'],
  ) {
    super(owner, args);

    this.possibleColumns = {
      CHANNEL: {
        name: this.intl.t('parity.channel'),
        valuePath: COLUMNS.CHANNEL,
        tdComponent: ParityTablesCellsTdOta,
        textAlign: 'left',
      },
      HOTEL: {
        name: this.intl.t('general.hotel.plural', { count: 1 }),
        valuePath: COLUMNS.HOTEL,
        textAlign: 'left',
      },
      META: {
        name: this.intl.t('general.meta'),
        valuePath: COLUMNS.META,
        textAlign: 'left',
      },
      SHOPPED_DATE: {
        name: this.intl.t('general.shopped_date'),
        valuePath: COLUMNS.SHOPPED_DATE,
        textAlign: 'left',
      },
      ARRIVAL_DATE: {
        name: this.intl.t('general.arrival_date'),
        valuePath: COLUMNS.ARRIVAL_DATE,
        textAlign: 'left',
      },
      BRAND_RATE: {
        name: this.intl.t('parity.base_rate'),
        valuePath: COLUMNS.BRAND_RATE,
        tdComponent: GeneralTablesCellsTdLink,
        thClassNames: 'pr-700',
        tdClassNames: 'stop-propagation-to-drawer',
        textAlign: 'right',
      },
      META_RATE: {
        name: this.intl.t('general.meta_rate'),
        valuePath: COLUMNS.META_RATE,
        tdComponent: GeneralTablesCellsTdLink,
        thClassNames: 'pr-700',
        tdClassNames: 'stop-propagation-to-drawer',
        textAlign: 'right',
      },
      CHANNEL_RATE: {
        name: this.intl.t('parity.channel_rate'),
        valuePath: COLUMNS.CHANNEL_RATE,
        tdComponent: GeneralTablesCellsTdLink,
        thClassNames: 'pr-700',
        tdClassNames: 'stop-propagation-to-drawer',
        textAlign: 'right',
        minWidth: 120,
      },
      DIFFERENCE: {
        name: this.intl.t('general.difference'),
        valuePath: COLUMNS.DIFFERENCE,
        tdComponent: GeneralTablesCellsTdWithCustomClass,
        tdClassNames: 'tabular-nums',
        textAlign: 'right',
      },
      ROOT_CAUSE: {
        name: this.intl.t('parity_manager.rootcause'),
        valuePath: COLUMNS.ROOT_CAUSE,
        textAlign: 'left',
      },
      WHOLESALER: {
        name: this.intl.t('parity_manager.stats.wholesaler'),
        valuePath: COLUMNS.ROOT_CAUSE,
        textAlign: 'left',
      },
      STATUS: {
        name: this.intl.t('parity_manager.status'),
        valuePath: COLUMNS.STATUS,
        textAlign: 'left',
      },
      ASSIGNED: {
        name: this.intl.t('general.assigned'),
        valuePath: COLUMNS.ASSIGNED,
        tdComponent: GeneralTablesCellsTdCheckmark,
        textAlign: 'left',
      },
      ACTIONS: {
        name: '',
        valuePath: COLUMNS.ACTIONS,
        tdComponent: GeneralTablesCellsTdWithActions,
        width: 82, // Minimum amount of space required to show actions
      },
    };
  }

  trackLinkExplored(column: Column): void {
    this.usageAnalytics.track('Link Explored', {
      eventTrigger: 'redirect-link',
      rateType: column,
    });
  }

  <template>
    {{#if @issues}}
      <PrismTable
        @columns={{this.columns}}
        @onRowClick={{@onRowClick}}
        @removeTableBorder={{true}}
        @rows={{this.rows}}
        class="pm-issue-table"
      />
    {{/if}}
  </template>
}
